import {
  fetchAdministrationMarketplaceProducts,
  updateAdministrationMarketplaceProduct,
  createAdministrationMarketplacePricingRule,
  endAdministrationMarketplacePricingRule,
} from "../../../api/administrationMarketplace";
import { createResourceLoader } from "../../state/keyedResourceLoader";
import {
  refreshMarketplaceCatalogMutation,
  refreshMarketplacePricingMutation,
} from "./refresh";
import type {
  MarketplaceAdministrationSlice,
  MarketplaceCatalogSlice,
} from "./types";

const loader = createResourceLoader();

export const marketplaceCatalogInitialState = {
  products: [],
  productsHydrated: false,
  productsLoading: false,
  productsError: "",
  productMutationLoading: false,
  pricingMutationLoading: false,
};

export const resetMarketplaceCatalogRequests = loader.clear;

export const createMarketplaceCatalogSlice:
  MarketplaceAdministrationSlice<MarketplaceCatalogSlice> = (set, get) => ({
    ...marketplaceCatalogInitialState,
    loadProducts: (force = false) => loader.load({
      force,
      cached: get().productsHydrated,
      request: fetchAdministrationMarketplaceProducts,
      onStart: () => set({ productsLoading: true, productsError: "" }),
      onSuccess: (products) => set({ products, productsHydrated: true }),
      onError: (productsError) => set({ productsError }),
      onFinish: () => set({ productsLoading: false }),
      errorMessage: "Unable to load Marketplace products.",
    }),
    receiveProduct: (product) => {
      // A saved response is newer than any catalog read already in flight.
      loader.clear();
      set((state) => ({
        products: state.products.some((item) => item.id === product.id)
          ? state.products.map((item) => item.id === product.id ? product : item)
          : [...state.products, product],
        productsLoading: false,
        productsError: "",
      }));
    },
    updateProduct: async (productId, product) => {
      if (get().productMutationLoading) {
        throw new Error("A Marketplace product change is already in progress.");
      }
      set({ productMutationLoading: true });
      try {
        const updated = await updateAdministrationMarketplaceProduct(
          productId,
          product,
        );
        get().receiveProduct(updated);
        await refreshMarketplaceCatalogMutation(get());
        return updated;
      } finally {
        set({ productMutationLoading: false });
      }
    },
    createPricingRule: async (configuration) => {
      if (get().pricingMutationLoading) throw new Error("A Marketplace pricing change is already in progress.");
      set({ pricingMutationLoading: true });
      try {
        const rule = await createAdministrationMarketplacePricingRule(configuration);
        set((state) => ({
          products: state.products.map((product) => product.id === configuration.productId
            ? { ...product, pricingRules: [...product.pricingRules, rule] }
            : product),
        }));
        await refreshMarketplacePricingMutation(get());
        return rule;
      } finally {
        set({ pricingMutationLoading: false });
      }
    },
    endPricingRule: async (productId, ruleId, change) => {
      if (get().pricingMutationLoading) throw new Error("A Marketplace pricing change is already in progress.");
      set({ pricingMutationLoading: true });
      try {
        const rule = await endAdministrationMarketplacePricingRule(ruleId, change);
        set((state) => ({
          products: state.products.map((product) => product.id === productId
            ? { ...product, pricingRules: product.pricingRules.map((item) => item.id === rule.id ? rule : item) }
            : product),
        }));
        await refreshMarketplacePricingMutation(get());
        return rule;
      } finally {
        set({ pricingMutationLoading: false });
      }
    },
  });
